import { onBeforeMount, reactive, ref } from 'vue';
import { query } from '@kk/utils';
import { useThrottleFn } from '@vueuse/core';
import { type GetRankByIdResponse, type RankListType } from '../types/rankListType';
import { type StyleDetail as StyleDetailType } from '../types/styleDetail';
import { getRankById } from '../api';
import Toast from '@kk/toast';
import Bus, { busEvent } from '@kk-activity-factory/bus';
import globalData from '@kk-activity-factory/global-data';
import { formatModuleId } from '@kk-activity-factory/utils';
import { pageDisplayManager } from '@kk-activity-factory/page-display-manager';
import dialogController from '@kk/vue-dialog';

const dataCache = new Map<string, { list: Promise<GetRankByIdResponse>; time: number }>();

/**
 * 只进行一次定位
 */
let onlyOnceSeekInOneVisit = true;

export async function getRankDataById(
    topicRankId: number,
    rankDisplayType: number,
    displayModuleConfigId: string,
    pageIndex: number,
    pageSize: number
) {
    const key = `${topicRankId}_${rankDisplayType}_${displayModuleConfigId}_${pageSize}`;
    const cache = dataCache.get(key);
    let cachePromise;
    // 利用promise在前端进行1秒的缓存
    if (cache && new Date().getTime() - cache.time < 500) {
        cachePromise = cache.list;
    } else {
        cachePromise = getRankById(
            topicRankId,
            rankDisplayType,
            displayModuleConfigId,
            pageIndex,
            pageSize
        );
        dataCache.set(key, {
            list: cachePromise,
            time: new Date().getTime(),
        });
    }
    return cachePromise;
}

export function useRankData(
    topicRankId: number,
    rankDisplayType: number,
    moduleId: string,
    styleDetail: StyleDetailType,
    displayModuleConfigIdKey: string,
    pageIndex?: number,
    pageSize?: number
) {
    const key = `${topicRankId}_${rankDisplayType}`;
    const imgHost = ref('');
    const hasNext = ref(false);
    const remoteData = reactive<{ list: Array<RankListType> }>({ list: [] });
    const firstDataReady = ref(false);
    let currentPage = pageIndex || 0;

    async function getData() {
        try {
            const { data } = await getRankDataById(
                topicRankId,
                rankDisplayType,
                displayModuleConfigIdKey,
                currentPage,
                pageSize || 50
            );
            hasNext.value = data.hasNext;
            remoteData.list = data.topicInfos;
            imgHost.value = data.host;
        } catch (error) {
            console.error(error);
            if (error instanceof Error) {
                Toast.show(error.message || '服务器错误');
            } else {
                throw error;
            }
        }
    }

    function pageChange(newPage: number) {
        currentPage = newPage;
        return getData();
    }

    const refreshData = useThrottleFn(getData, 1300);

    async function seekShareInfo() {
        const topicRankModuleId = query.get('topic_rank_module_id');
        const topicId = query.get('topic_id');
        const inviteFrom = query.get('invite_from');
        console.info('seekShareInfo', moduleId, topicId, inviteFrom);
        if (topicRankModuleId) {
            // 还没有定位过模块的话, 尝试去定位一次模块
            if (onlyOnceSeekInOneVisit) {
                onlyOnceSeekInOneVisit = false;
                pageDisplayManager.activeModule(topicRankModuleId);
            }
            if (globalData.isOutside) {
                // 端外逻辑-并且是自己
                if (formatModuleId(topicRankModuleId) === formatModuleId(moduleId)) {
                    // 走打开弹窗逻辑
                    const cache = dataCache.get(key);
                    if (cache) {
                        const {
                            data: { topicInfos, host },
                        } = await cache.list;
                        console.info(topicInfos, host);
                        const info = topicInfos.find((item) => item.topicId === Number(topicId));
                        if (info) {
                            // 查找推荐语信息
                            const btnAction = styleDetail.buttonActions.find((item) => {
                                return item.recommendText && item.recommendText.length > 0;
                            });
                            if (btnAction) {
                                const body = document.body;
                                body.style.overflow = 'hidden';
                                dialogController.asyncShow({
                                    component: () =>
                                        import(
                                            /* webpackChunkName: "topic-dialog" */ '../TopicDialog.vue'
                                        ),
                                    props: {
                                        withCloseBtn: false,
                                    },
                                    contentProps: {
                                        cover: host + info.topicImg,
                                        titleColor: btnAction.nameColor,
                                        dialogTitle: info.topicName,
                                        recommendTextColor: btnAction.recommendColor,
                                        recommendText: btnAction.recommendText,
                                        nicknameColor: btnAction.nickColor,
                                        outsideBtnImgUrl: btnAction.guideButtonImg?.imgUrl,
                                        closeBtnImgUrl: btnAction.closeButtonImg?.imgUrl,
                                        backgroundColor: btnAction.rebgColor,
                                        userId: inviteFrom,
                                        topicRankModuleId: topicRankModuleId,
                                        topicId: topicId,
                                    },
                                    events: {
                                        afterLeave: () => {
                                            body.style.overflow = 'auto';
                                        },
                                    },
                                });
                            }
                        }
                    }
                    console.info('dataCache', dataCache.get(key));
                } else {
                    console.info(`seekShareInfo:${moduleId}和${topicRankModuleId}不匹配`);
                }
            }
        }
    }

    onBeforeMount(async () => {
        await getData();
        Bus.$on(busEvent.RECEIVE_PAGE_STATUS, (res: { data: { curr_page_is_visible: number } }) => {
            if (res?.data?.curr_page_is_visible === 1) {
                refreshData();
            }
        });
        Bus.$on(busEvent.GLOBAL_COIN_MAYBE_CHANGE, () => {
            refreshData();
        });
        Bus.$on(busEvent.UPDATE_SUBJECT_FOLLOW_STATUS, () => {
            refreshData();
        });
        await seekShareInfo();
        setTimeout(() => {
            firstDataReady.value = true;
        }, 600);
    });

    return {
        imgHost,
        hasNext,
        remoteData,
        firstDataReady,
        refreshData,
        pageChange,
    };
}
